import React, { useEffect, useState } from "react";
import Loading from "../../../components/Loading";
import Menu from "../../../components/Menu";
import TablaArchivos from "./Tabla";
import archivo_icon_active from "../../../assets/vw/svg/archivo_azul.svg";
import before from "../../../assets/vw/svg/before-mon-azul.svg";
import cerrar from "../../../assets/vw/svg/cerrar.svg";
import download_icon from "../../../assets/vw/svg/download-white.svg";

const normalizePath = (path = "") => path.replace(/^\/+/, "").replace(/\/+/g, "/");

const normalizeFolderKey = (path = "") => {
    const normalizedPath = normalizePath(path).replace(/\/$/, "");
    return normalizedPath ? `${normalizedPath}/` : "";
};

const getFileName = (key = "") => key.split("/").filter(Boolean).pop() || key;

const COPY_STORAGE_KEY = "libreria-copiar-item";

const parseResponse = async (response) => {
    const text = await response.text();
    if (!text) return null;

    try {
        const data = JSON.parse(text);
        if (data?.body) {
            return typeof data.body === "string" ? JSON.parse(data.body) : data.body;
        }
        return data;
    } catch {
        return text;
    }
};

const normalizeBackendRows = (data) => {
    const carpetas = (data?.carpetas || []).map((carpeta) => ({
        Key: normalizeFolderKey(carpeta.key),
        LastModified: carpeta.fechaModificacion || new Date().toISOString(),
        Size: carpeta.size || 0,
        StorageClass: "STANDARD",
        cantidadObjetos: carpeta.cantidadObjetos,
    }));

    const archivos = (data?.archivos || []).map((archivo) => ({
        Key: normalizePath(archivo.key),
        LastModified: archivo.fechaModificacion,
        Size: archivo.tamano,
        StorageClass: "STANDARD",
    }));

    return [...carpetas, ...archivos];
};

const normalizeFolderOptions = (data) => (data?.carpetas || []).map((carpeta) => ({
    label: carpeta.nombre || carpeta.key?.replace(/\/$/, "").split("/").pop(),
    value: normalizeFolderKey(carpeta.key),
}));

const getFolderName = (key = "") => key.replace(/\/$/, "").split("/").filter(Boolean).pop() || key;

function Libreria({ servicio }) {
    const backendUrl = process.env.REACT_APP_URL_DTO_BACK;
    const [isLoading, setIsLoading] = useState(false);
    const [currentPrefix, setCurrentPrefix] = useState("");
    const [archivos, setArchivos] = useState([]);
    const [copiedItem, setCopiedItem] = useState(null);
    const [preview, setPreview] = useState({
        active: false,
        url: "",
        key: "",
        name: "",
    });

    useEffect(() => {
        try {
            const storedItem = localStorage.getItem(COPY_STORAGE_KEY);
            if (storedItem) setCopiedItem(JSON.parse(storedItem));
        } catch (error) {
            console.error(error.stack || error);
            localStorage.removeItem(COPY_STORAGE_KEY);
        }
    }, []);

    const requestArchivos = async (endpoint, options = {}) => {
        const response = await fetch(`${backendUrl}archivos/${endpoint}`, {
            ...options,
            headers: {
                Authorization: "Bearer " + servicio.getToken(),
                ...(options.body ? { "Content-Type": "application/json" } : {}),
                ...(options.headers || {}),
            },
        });
        const data = await parseResponse(response);

        if (!response.ok) {
            throw new Error(data?.mensaje || data?.message || "Error al procesar la solicitud");
        }

        return data;
    };

    const loadArchivos = async (prefix = currentPrefix) => {
        setIsLoading(true);
        try {
            const queryParams = new URLSearchParams();
            if (prefix) queryParams.set("path", prefix);

            const data = await requestArchivos(`listar${queryParams.toString() ? `?${queryParams}` : ""}`, {
                method: "GET",
            });
            setArchivos(normalizeBackendRows(data));
        } catch (error) {
            console.error(error.stack || error);
            setArchivos([]);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        loadArchivos(currentPrefix);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPrefix]);

    const handleRegresar = (event) => {
        event.preventDefault();
        if (!currentPrefix) return;

        const segments = currentPrefix.replace(/\/$/, "").split("/");
        segments.pop();
        setCurrentPrefix(segments.length > 0 ? `${segments.join("/")}/` : "");
    };

    const currentTitle = currentPrefix
        ? currentPrefix.replace(/\/$/, "")
        : "Archivos";

    const handleNuevoProyecto = async (nombre = "") => {
        const nombreLimpio = nombre.trim();
        if (!nombreLimpio || nombreLimpio.includes("/")) return;

        setIsLoading(true);
        try {
            await requestArchivos("nueva-carpeta", {
                method: "POST",
                body: JSON.stringify({ key: normalizeFolderKey(nombreLimpio) }),
            });
            await loadArchivos();
            return true;
        } catch (error) {
            console.error(error.stack || error);
            setIsLoading(false);
            return false;
        }
    };

    const handleNuevaCarpeta = async (nombre = "", parentPrefix = currentPrefix) => {
        const nombreLimpio = nombre.trim();
        if (!nombreLimpio || nombreLimpio.includes("/")) return;

        const folderPrefix = normalizeFolderKey(parentPrefix);

        setIsLoading(true);
        try {
            await requestArchivos("nueva-carpeta", {
                method: "POST",
                body: JSON.stringify({ key: normalizeFolderKey(`${folderPrefix}${nombreLimpio}`) }),
            });
            await loadArchivos();
        } catch (error) {
            console.error(error.stack || error);
            setIsLoading(false);
        }
    };

    const handleSubir = async (file) => {
        if (!file) return;

        const key = normalizePath(`${currentPrefix}${file.name}`);
        setIsLoading(true);
        try {
            const data = await requestArchivos("subir", {
                method: "POST",
                body: JSON.stringify({ key, contentType: file.type || "application/octet-stream" }),
            });

            const uploadResponse = await fetch(data.url, {
                method: data.method || "PUT",
                headers: {
                    "Content-Type": file.type || "application/octet-stream",
                },
                body: file,
            });

            if (!uploadResponse.ok) {
                throw new Error("Error al subir el archivo a S3");
            }

            await loadArchivos();
        } catch (error) {
            console.error(error.stack || error);
            setIsLoading(false);
        }
    };

    const handleDescargar = async (archivo) => {
        if (!archivo) return;

        setIsLoading(true);
        try {
            const key = archivo.__prefix || archivo.Key;
            const queryParams = new URLSearchParams({ key, download: "true" });
            const data = await requestArchivos(`descargar?${queryParams}`, {
                method: "GET",
            });

            window.open(data.url, "_blank", "noopener,noreferrer");
        } catch (error) {
            console.error(error.stack || error);
        } finally {
            setIsLoading(false);
        }
    };

    const handlePreview = async (archivo) => {
        setIsLoading(true);
        try {
            const queryParams = new URLSearchParams({ key: archivo.Key });
            const data = await requestArchivos(`descargar?${queryParams}`, {
                method: "GET",
            });

            setPreview({
                active: true,
                url: data.url,
                key: archivo.Key,
                name: getFileName(archivo.Key),
            });
        } catch (error) {
            console.error(error.stack || error);
        } finally {
            setIsLoading(false);
        }
    };

    const closePreview = () => {
        setPreview({
            active: false,
            url: "",
            key: "",
            name: "",
        });
    };

    const handleEliminar = async (archivo) => {
        setIsLoading(true);
        try {
            const queryParams = new URLSearchParams({ key: archivo.__prefix || archivo.Key });
            await requestArchivos(`borrar?${queryParams}`, {
                method: "DELETE",
            });
            await loadArchivos();
        } catch (error) {
            console.error(error.stack || error);
            setIsLoading(false);
        }
    };

    const handleMover = async (archivo, destino) => {
        if (!archivo) return;

        const destinoPrefix = normalizeFolderKey(destino);
        const origen = archivo.__prefix || archivo.Key;
        const isFolder = archivo.__isPrefix || origen.endsWith("/");

        if (isFolder && destinoPrefix.startsWith(origen)) return;

        const destinationKey = isFolder
            ? normalizeFolderKey(`${destinoPrefix}${getFolderName(origen)}`)
            : normalizePath(`${destinoPrefix}${getFileName(origen)}`);

        if (origen === destinationKey) return;

        setIsLoading(true);
        try {
            await requestArchivos("mover", {
                method: "PUT",
                body: JSON.stringify({ origen, destino: destinationKey }),
            });
            await loadArchivos();
        } catch (error) {
            console.error(error.stack || error);
            setIsLoading(false);
        }
    };

    const handleCopiar = (archivo) => {
        if (!currentPrefix) return;
        if (!archivo) return;

        const origen = archivo.__prefix || archivo.Key;
        const item = {
            key: origen,
            isFolder: archivo.__isPrefix || origen.endsWith("/"),
        };

        localStorage.setItem(COPY_STORAGE_KEY, JSON.stringify(item));
        setCopiedItem(item);
    };

    const handlePegar = async () => {
        if (!currentPrefix) return;
        if (!copiedItem) return;

        const origen = copiedItem.key;
        const isFolder = copiedItem.isFolder || origen.endsWith("/");
        const destino = isFolder
            ? normalizeFolderKey(`${currentPrefix}${getFolderName(origen)}`)
            : normalizePath(`${currentPrefix}${getFileName(origen)}`);

        if (origen === destino) return;
        if (isFolder && currentPrefix.startsWith(origen)) return;

        setIsLoading(true);
        try {
            await requestArchivos("mover", {
                method: "PUT",
                body: JSON.stringify({ origen, destino, copiar: true }),
            });
            localStorage.removeItem(COPY_STORAGE_KEY);
            setCopiedItem(null);
            await loadArchivos();
        } catch (error) {
            console.error(error.stack || error);
            setIsLoading(false);
        }
    };

    const canPaste = Boolean(copiedItem) && Boolean(currentPrefix) && (() => {
        const origen = copiedItem.key;
        const isFolder = copiedItem.isFolder || origen.endsWith("/");
        const destino = isFolder
            ? normalizeFolderKey(`${currentPrefix}${getFolderName(origen)}`)
            : normalizePath(`${currentPrefix}${getFileName(origen)}`);

        return origen !== destino && !(isFolder && currentPrefix.startsWith(origen));
    })();

    const handleRenombrar = async (archivo, nuevoNombre = "") => {
        if (!archivo) return;

        const nombreLimpio = nuevoNombre.trim();
        if (!nombreLimpio || nombreLimpio.includes("/")) return;

        setIsLoading(true);
        try {
            await requestArchivos("renombrar", {
                method: "PUT",
                body: JSON.stringify({ origen: archivo.__prefix || archivo.Key, nuevoNombre: nombreLimpio }),
            });
            await loadArchivos();
        } catch (error) {
            console.error(error.stack || error);
            setIsLoading(false);
        }
    };

    const handleListarCarpetas = async (path = "") => {
        const queryParams = new URLSearchParams();
        if (path) queryParams.set("path", path);

        const data = await requestArchivos(`listar${queryParams.toString() ? `?${queryParams}` : ""}`, {
            method: "GET",
        });

        return normalizeFolderOptions(data);
    };

    return (
        <>
            <Menu />
            {isLoading && <Loading />}
            <div className="navegacion libreria">
                <div className="posicionMenu">
                    {currentPrefix && (
                        <a
                            href="/vw/especialista/libreria"
                            className="returnToMon d-inline-flex align-items-center mb-2"
                            onClick={handleRegresar}
                        >
                            <img src={before} alt="before" className="mr-2" />
                            Regresar
                        </a>
                    )}
                    <ul className="d-flex flex-row px-0 my-0">
                        <li className="opcion-ruta">
                            <div className="nav-link nav-link-title active ps-0 active border_menu">
                                <img
                                    src={archivo_icon_active}
                                    alt="archivos"
                                    className="icono-menu me-3"
                                />
                                {currentTitle}
                            </div>
                        </li>
                    </ul>
                </div>
                <div className="row border_vw pt-4 m-0">
                    <div className="col-12 p-0 contenedor-table">
                        <TablaArchivos
                            archivos={archivos}
                            currentPrefix={currentPrefix}
                            setCurrentPrefix={setCurrentPrefix}
                            onNuevo={handleNuevoProyecto}
                            onSubir={handleSubir}
                            onNuevaCarpeta={handleNuevaCarpeta}
                            onDescargar={handleDescargar}
                            onEliminar={handleEliminar}
                            onMover={handleMover}
                            onCopiar={handleCopiar}
                            onPegar={handlePegar}
                            onRenombrar={handleRenombrar}
                            onListarCarpetas={handleListarCarpetas}
                            onPreview={handlePreview}
                            canPaste={canPaste}
                        />
                    </div>
                </div>
            </div>
            {preview.active && (
                <div className="libreria-preview-window">
                    <div className="libreria-preview-header">
                        <span>Vista previa</span>
                        <button className="button_icon-vw" type="button" onClick={closePreview} aria-label="Cerrar vista previa">
                            <img src={cerrar} alt="cerrar" />
                        </button>
                    </div>
                    <div className="libreria-preview-body">
                        <img src={preview.url} alt={preview.name} />
                    </div>
                    <div className="libreria-preview-footer">
                        <button type="button" className="button_vw button_vw--primary" onClick={() => handleDescargar({ Key: preview.key })}>
                            <img src={download_icon} alt="descargar" className="me-2" />
                            Descargar
                        </button>
                    </div>
                </div>
            )}
        </>
    );
}

export default Libreria;
